import _ from 'underscore';
import classNames from 'classnames';
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Image, Checkbox, Text, DropdownMenuItemType } from '@fluentui/react';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { formatGTMContainers } from '@bingads-webui-campaign-react/conversion-utils';

import { GoogleLoginButton } from '../components/google-login-button/google-login-button';
import { GoogleLoginRepository } from '../repository/google-login-repository';
import { loadGoogleAPIScript } from '../repository/google-login-init';
import { GoogleTagManagerReporitory } from '../repository/gtm-repository';
import { getGtmInstructionsStyles } from '../styles/gtm-instructions-styles';
import { chromeExtURL, edgeExtURL } from '../utils/uet-tag-helper-install-urls';
import extIconImage from '../../media/extPic.png';

const LearnMore = (props) => {
  const { i18n, topicId, ariaLabel } = props;

  return (
    <React.Fragment>
      <span> </span>
      <button
        className="HelpTopic clickable btn-link"
        query={`help:${topicId}`}
        aria-label={ariaLabel}
      >
        {i18n.getString(_TL_('Learn more'))}
      </button>
    </React.Fragment>);
};

LearnMore.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  topicId: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const GoogleTagManagerInstructionsUnstyled = (props) => {
  const {
    classes,
    tag,
    serverContext,
    onGoogleLoggedOut,
    onGoogleSelectAccount,
    deps,
    updateGTMImportContext,
    finishPageData,
    gtmUIContext,
    logTrace,
  } = props;

  const i18n = useContext(I18nContext);
  const completedHelpLabel = i18n.getString(_TL_('Next, you can check your UET tag status using the UET Tag Helper. You can install this browser extension on Microsoft Edge or Google Chrome. Learn more.'));
  const completedTestInstructionTemplate = i18n.getString(_TL_('Next, you can check your UET tag status using the UET Tag Helper. You can install this browser extension on {{m}}Microsoft Edge{{/m}} or {{g}}Google Chrome{{/g}}.'));
  const inProgressHelpLabel = i18n.getString(_TL_('It will take some time to set up. Next, you can check your UET tag status using the UET Tag Helper. You can install this browser extension on Microsoft Edge or Google Chrome. Learn more.'));
  const inProgressTestInstructionTemplate = i18n.getString(_TL_('It will take some time to set up. Next, you can check your UET tag status using the UET Tag Helper. You can install this browser extension on {{m}}Microsoft Edge{{/m}} or {{g}}Google Chrome{{/g}}.'));
  const trackingInstructionTemplate = i18n.getString(_TL_('We can use this UET tag to track both conversions and audiences based on actions people take on your website. Please {{c}}Create a conversion goal{{/c}} or {{a}}Create audience{{/a}} to get started.'));

  const { AdvertiserCustomerID: cid, Id: aid } = serverContext && serverContext.CurrentAccount;
  const conversionGoalsPageLink = document.URL.replace(/Smart\?.*conversiontracking$/, `vnext/conversiongoals?cid=${cid}&aid=${aid}`);
  const audiencesLibraryPageLink = document.URL.replace(/Smart\?.*conversiontracking$/, `vnext/audienceslibrary?cid=${cid}&aid=${aid}`);

  const { token, repository, isAllDependenciesLoaded } = deps;
  const [loginRepository, setLoginRepository] = useState(null);
  const [isGoogleAPIInitialized, setIsGoogleAPIInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [emailAddress, setEmailAddress] = useState(null);
  const [googleSessionId, setGoogleSessionId] = useState(null);
  const [accountList, setAccountList] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [containerList, setContainerList] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [shouldBlockerLoading, setShouldBlockerLoading] = useState(false);
  const [isGTMImprovementEnabled, setIsGTMImprovementEnabled] = useState(false);

  const updateAccountSelection = useCallback((GTMAccountId, GTMAccountName) => {
    setSelectedAccount(GTMAccountId);
    setSelectedContainer(null);
    setContainerList(null);
    updateGTMImportContext({
      loginEmail: emailAddress,
      GTMAccountName,
      GTMContainerName: null,
      requestBody: {
        SessionId: googleSessionId,
        GTMAccountId,
        GTMContainerId: null,
      },
    });
  }, [emailAddress, googleSessionId, updateGTMImportContext]);

  const updateContainerSelection = useCallback((GTMContainerId, GTMContainerName) => {
    setSelectedContainer(GTMContainerId);

    updateGTMImportContext({
      loginEmail: emailAddress,
      GTMContainerName,
      requestBody: {
        SessionId: googleSessionId,
        UetTagId: tag.id,
        GTMAccountId: selectedAccount,
        GTMContainerId,
      },
    });
  }, [emailAddress, googleSessionId, selectedAccount, tag.id, updateGTMImportContext]);

  useEffect(() => {
    if (finishPageData) {
      setCurrentStep(2);
    } else if (gtmUIContext.sessionId) {
      setGoogleSessionId(gtmUIContext.sessionId);
      setEmailAddress(gtmUIContext.loginEmail);
      setCurrentStep(1);
    }
  }, [finishPageData, gtmUIContext.loginEmail, gtmUIContext.sessionId]);

  useEffect(() => {
    if (!loginRepository && !isGoogleAPIInitialized) {
      setIsGoogleAPIInitialized(true);
      loadGoogleAPIScript('https://accounts.google.com/gsi/client')
        .then(() => {
          logTrace(`GSILocal: ${serverContext.DynamicPermissions.GSILocal}`);
          setLoginRepository(new GoogleLoginRepository({
            deps: {
              google: window.google,
              permissions: serverContext.Permissions,
              GSILocal: serverContext.DynamicPermissions.GSILocal,
            },
          }));
          logTrace('[GoogleTagManager] Initialized GoogleLoginRepository');
        }).catch((err) => {
          logTrace(`[GoogleTagManager] Failed to initialize GoogleLoginRepository: ${err}`);
        });
    }
  }, [
    isGoogleAPIInitialized,
    loginRepository,
    logTrace,
    serverContext,
  ]);

  const onChangeAccount = useCallback((event, { key: GTMAccountId, text: GTMAccountName }) => {
    if (googleSessionId === null || _.isEmpty(GTMAccountId) || GTMAccountId === selectedAccount) {
      return;
    }
    updateAccountSelection(GTMAccountId, GTMAccountName);
    setShouldBlockerLoading(true);

    // call containers API
    repository.getGTMContainers(googleSessionId, GTMAccountId)
      .then((res) => {
        if (_.isArray(res)) {
          if (res.length > 0) {
            setContainerList(formatGTMContainers({ GTMContainers: res }));
          } else {
            logTrace(`[GoogleTagManager] Get GTM containers error: No GTM containers under ${GTMAccountId}`);
            notificationApi.showWarning(i18n.getString(_TL_('This GTM account doesn\'t have any containers. Please select another account that has containers')));
          }
        } else if (!_.isUndefined(res.GTMContainers)) {
          if (res.GTMContainers.length > 0) {
            setContainerList(formatGTMContainers(res));
            setIsGTMImprovementEnabled(true);
          } else {
            logTrace(`[GoogleTagManager] Get GTM containers error: No GTM containers under ${GTMAccountId}`);
            notificationApi.showWarning(i18n.getString(_TL_('This GTM account doesn\'t have any containers. Please select another account that has containers')));
          }
        }
      })
      .catch((err) => {
        logTrace(`[GoogleTagManager] Get GTM containers error: ${JSON.stringify(err)}`);
        notificationApi.showWarning(i18n.getString(_TL_('There is an error during fetching GTM containers of selected GTM account')));
      })
      .finally(() => {
        setShouldBlockerLoading(false);
      });
  }, [googleSessionId, i18n, logTrace, repository, selectedAccount, updateAccountSelection]);

  const handleGTMAccountsResp = useCallback((res) => {
    if (res.length > 0) {
      setAccountList(res);
    } else {
      logTrace('[GoogleTagManager] Get GTM accounts error: No GTM account');
      notificationApi.showWarning(i18n.getString(_TL_('This Google account doesn\'t have any GTM account. Please sign out and log in with a different google account')));
    }
    setCurrentStep(1);
  }, [i18n, logTrace]);

  useEffect(() => {
    if (accountList && accountList.length && !selectedAccount) {
      onChangeAccount(null, { key: accountList[0].AccountId, text: accountList[0].Name });
    }
  }, [accountList, onChangeAccount, selectedAccount]);

  useEffect(() => {
    if (containerList && !selectedContainer) {
      const { allContainers } = containerList;
      if (!isGTMImprovementEnabled) {
        if (allContainers.length) {
          updateContainerSelection(allContainers[0].ContainerId, allContainers[0].Name);
        }
      } else {
        const newSelectedContainer = _.get(containerList, ['installedContainers', 0]) || _.get(containerList, ['allContainers', 0]);
        if (newSelectedContainer) {
          updateContainerSelection(newSelectedContainer.ContainerId, newSelectedContainer.Name);
        }
      }
    }
  }, [containerList, selectedContainer, updateContainerSelection, isGTMImprovementEnabled]);

  const processAfterSignIn = useCallback((startGoogleSessionPromise) => {
    startGoogleSessionPromise
      .then((sessionId) => {
        if (sessionId) {
          repository.fetchGoogleLoginEmail(sessionId).then((email) => {
            setGoogleSessionId(sessionId);
            setEmailAddress(email);
            updateGTMImportContext({
              loginEmail: email,
              requestBody: {
                SessionId: sessionId,
              },
            });
          }).catch((err) => {
            logTrace(`[GoogleTagManager] Get Google Email failed. Error: ${JSON.stringify(err)}`);
            setShouldBlockerLoading(false);
          });
        }
      })
      .catch((err) => {
        logTrace(`[GoogleTagManager] Start Google session error: ${JSON.stringify(err)}`);
        setShouldBlockerLoading(false);
      });
  }, [logTrace, repository, updateGTMImportContext]);

  const logoutEndpoint = useCallback(() => {
    window.frames.logoutIFrame.location = 'https://www.google.com/accounts/Logout';
  }, []);

  const logoutGoogle = useCallback(() => {
    logTrace('[GoogleTagManager] Sign out button clicked.');
    logoutEndpoint();
    setCurrentStep(0);
    onGoogleLoggedOut();
    updateGTMImportContext({
      loginEmail: null,
      GTMContainerName: null,
      GTMAccountName: null,
      requestBody: {
        SessionId: null,
        GTMAccountId: null,
        GTMContainerId: null,
      },
    });
    setGoogleSessionId(null);
    setAccountList(null);
    setContainerList(null);
    setSelectedAccount(null);
    setSelectedContainer(null);
  }, [logTrace, logoutEndpoint, onGoogleLoggedOut, updateGTMImportContext]);

  const checkScopeList = useCallback(scope => scope.indexOf('tagmanager.edit.containerversions') > -1
    && scope.indexOf('tagmanager.publish') > -1
    && scope.indexOf('tagmanager.edit.containers') > -1, []);

  const onGoogleAuthSuccess = useCallback((response) => {
    // check if user granted all permissions
    const scopeString = response.scope;
    if (!checkScopeList(scopeString)) {
      logoutEndpoint();
      logTrace('[GoogleTagManager] User didn\'t check all permissions');
      notificationApi.showWarning(i18n.getString(_TL_('Microsoft Advertising has encountered an error. Please sign in to Google again and ensure that you have checked all the permissions as they are required for us to set up your UET tags properly.')));
      return;
    }
    logTrace('[GoogleTagManager] Sign in Google Succeeded.');
    // show ui blocker
    setShouldBlockerLoading(true);
    // start google session and get account list
    const authCode = response.code;
    if (isAllDependenciesLoaded) {
      processAfterSignIn(repository.startGoogleSession(authCode));
    }
  }, [checkScopeList, i18n, isAllDependenciesLoaded, logTrace, logoutEndpoint, processAfterSignIn, repository]);

  const signInToGoogleWithAuth = useCallback(() => {
    if (loginRepository) {
      setShouldBlockerLoading(true);
      new Promise((resolve, reject) => {
        loginRepository.googleSignIn({
          onSuccess: resolve,
          onFail: reject,
          useConsentPrompt: true,
        });
      }).then(onGoogleAuthSuccess)
        .catch((err) => {
          logTrace(`[GoogleTagManager] Sign in Google Failed. Error: ${JSON.stringify(err)}`);
          setShouldBlockerLoading(false);
        });
    } else {
      notificationApi.showWarning(i18n.getString(_TL_('Microsoft Advertising has encountered an error. Please refresh the page and try again.')));
    }
  }, [logTrace, loginRepository, onGoogleAuthSuccess, i18n]);

  const signInToGoogle = useCallback(() => {
    logTrace('[GoogleTagManager] Sign in button clicked');
    const testEmailAddress = 'testAccount@gmail.com';

    if (serverContext.isDemo) {
      setEmailAddress(testEmailAddress);
      setAccountList([{ AccountId: '1', Name: 'UETSampleAccount' }, { AccountId: '2', Name: 'SecondTestAccount' }]);
      setContainerList({ allContainers: [{ ContainerId: '1', Name: 'UITestingContainer' }, { ContainerId: '2', Name: 'SecondTestContainer' }] });
      updateGTMImportContext({
        loginEmail: testEmailAddress,
        requestBody: {
          SessionId: 'test-session-id',
        },
      });
    } else if (token) {
      setEmailAddress(testEmailAddress);
      processAfterSignIn(repository.startGoogleSessionWithToken(token));
    } else {
      signInToGoogleWithAuth();
    }
  }, [logTrace, processAfterSignIn, repository, serverContext.isDemo, signInToGoogleWithAuth, token, updateGTMImportContext]);

  useEffect(() => {
    if (isAllDependenciesLoaded && googleSessionId && _.isNull(accountList)) {
      // get GTMAccount
      repository.getGTMAccounts(googleSessionId)
        .then((res) => {
          handleGTMAccountsResp(res);
        });
    }
  }, [accountList, googleSessionId, handleGTMAccountsResp, isAllDependenciesLoaded, repository]);

  const accountsDropdownOptions = useMemo(() => {
    const options = [];
    _.each(accountList, (obj) => {
      options.push({ key: obj.AccountId, text: obj.Name });
    });
    return options;
  }, [accountList]);


  const containersDropdownOptions = useMemo(() => {
    const options = [];

    if (!isGTMImprovementEnabled) {
      _.each(_.get(containerList, 'allContainers'), (obj) => {
        options.push({ key: obj.ContainerId, text: obj.Name });
      });
    } else {
      if (containerList && containerList.installedContainers && containerList.installedContainers.length) {
        options.push({ key: 'installed', text: i18n.getString('Installation detected (recommended)'), itemType: DropdownMenuItemType.Header });
        _.each(_.get(containerList, 'installedContainers'), (obj) => {
          options.push({ key: obj.ContainerId, text: obj.Name });
        });
        options.push({ key: 'divider_1', text: '-', itemType: DropdownMenuItemType.Divider });
      }

      if (containerList && containerList.others && containerList.others.length) {
        options.push({ key: 'no-detected', text: i18n.getString('No installation detected'), itemType: DropdownMenuItemType.Header });
        _.each(_.get(containerList, 'others'), (obj) => {
          options.push({ key: obj.ContainerId, text: obj.Name });
        });
      }
    }

    return options;
  }, [containerList, i18n, isGTMImprovementEnabled]);

  const onChangeContainer = useCallback((event, option) => {
    updateContainerSelection(option.key, option.text);
  }, [updateContainerSelection]);

  const onTagCloningCheckboxChange = useCallback((event, isChecked) => {
    updateGTMImportContext({ requestBody: {}, cloneExistingTags: isChecked });
  }, [updateGTMImportContext]);

  const generateFinishPage = useMemo(() => {
    if (finishPageData) {
      const tagManagerLink = `https://tagmanager.google.com/#/versions/accounts/${selectedAccount}/containers/${selectedContainer}/versions/${finishPageData.publishedVersionId}`;
      return (
        <div className={classes.container}>
          <div className={classes.contentBlock}>
            <p className={classes.textBlock}>
              <ReactTemplate
                model={{
                  m: text => (<a href={edgeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
                  g: text => (<a href={chromeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
                }}
                template={finishPageData.publishedVersionId ? completedTestInstructionTemplate : inProgressTestInstructionTemplate}
              />
              <LearnMore i18n={i18n} topicId="app54548" ariaLabel={finishPageData.publishedVersionId ? completedHelpLabel : inProgressHelpLabel} />
            </p>
            <div className={classes.imageCenterBlock}>
              <Image src={extIconImage} alt={i18n.getString(_TL_('Extension icon'))} />
            </div>
            <p className={classes.textBlock}>
              <ReactTemplate
                model={{
                  c: text => (<a href={conversionGoalsPageLink}>{text}</a>),
                  a: text => (<a href={audiencesLibraryPageLink}>{text}</a>),
                }}
                template={trackingInstructionTemplate}
              />
            </p>
            {finishPageData.publishedVersionId && (
              <p className={classes.textBlock}>
                {i18n.getString(_TL_('You can see the tags created in your Google Tag Manager container on the latest published version. You can also use this link to see the changes:'))}
                {' '}<a target="_blank" rel="noopener noreferrer" href={tagManagerLink}>{tagManagerLink}</a>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  }, [classes, completedHelpLabel, completedTestInstructionTemplate, finishPageData, i18n, inProgressHelpLabel, inProgressTestInstructionTemplate, trackingInstructionTemplate, selectedAccount, selectedContainer, audiencesLibraryPageLink, conversionGoalsPageLink]);

  const generateStepContent = useMemo(() => {
    if (finishPageData) {
      return (
        <div id="gtm-finish-page">{generateFinishPage}</div>
      );
    }
    if (currentStep === 0) {
      onGoogleLoggedOut();
      return (
        <div className={classes.container} id="gtm-login-page">
          <div className={classes.contentBlock}>
            <p className={classes.textBlock}>{i18n.getString(_TL_('In order to set up tagging, we need one-time access to your Google Tag Manager account. When logging into Google, make sure to allow Microsoft Advertising to make changes to your Google Tag Manager account to continue setup.'))}</p>
          </div>
          <GoogleLoginButton
            i18n={i18n}
            onClick={signInToGoogle}
          />
        </div>
      );
    }
    if (currentStep === 1) {
      onGoogleSelectAccount();
      return (
        <div className={classes.container} id="gtm-preference-page">
          <div className={classes.contentBlock}>
            <p className={classes.textBlock}>{i18n.getString(_TL_('Select the Google Tag Manager account and container you want to integrate with. If you don\'t see the correct account information, make sure you are logged into the Google profile associated with the Google Tag Manager account you want to use.'))}</p>
          </div>
          {/* <PrimaryButton text={i18n.getString(_TL_('Sign out'))} onClick={logoutGoogle} /> */}
          <div className={classes.contentBlock}>
            <p className={classes.textBlock}>{i18n.getString(_TL_('You are signed in to this Google account:'))} <b>{emailAddress || ''}</b>
              <button
                className="clickable btn-link"
                onClick={logoutGoogle}
                aria-label={i18n.getString(_TL_('Sign out'))}
              >
                {i18n.getString(_TL_('Sign out'))}
              </button>
            </p>
            {!_.isEmpty(accountsDropdownOptions) &&
              <div className="gtm-account-dropdown">
                <Dropdown
                  className={classes.dropdown}
                  label={i18n.getString(_TL_('Account'))}
                  placeholder={i18n.getString(_TL_('Select an account...'))}
                  dropdownWidth={0}
                  options={accountsDropdownOptions}
                  selectedKey={selectedAccount}
                  onChange={onChangeAccount}
                />
              </div>
            }
          </div>
          {!_.isEmpty(containersDropdownOptions) && selectedAccount &&
            <div className={classNames(classes.contentBlock, 'gtm-container-dropdown')}>
              <Dropdown
                className={classes.dropdown}
                disabled={isGTMImprovementEnabled && _.get(containerList, 'installedContainers', []).length === 1}
                label={i18n.getString(_TL_('Container'))}
                placeholder={i18n.getString(_TL_('Select a container...'))}
                dropdownWidth={0}
                options={containersDropdownOptions}
                selectedKey={selectedContainer}
                onChange={onChangeContainer}
              />
            </div>
          }
          {gtmUIContext.AllowTagCloning && !_.isEmpty(selectedContainer) && (
            <div>
              <Checkbox
                label={i18n.getString(_TL_('(Recommended) Allow Microsoft Advertising UET tag to use my Universal Analytics, Google Ads Conversion Tracking, or Google Analytics GA4 Event tags used on the same container.'))}
                onChange={onTagCloningCheckboxChange}
                checked={gtmUIContext.IsGTMCloningChecked}
              />
              <div style={{ paddingLeft: 27 }}>
                <Text>{i18n.getString(_TL_('Your UET tags will use the same set up options including the tag settings and trigger values used for Universal Analytics or Google Analytics GA4 Event tags.'))}</Text>
              </div>
            </div>
          ) }
        </div>
      );
    }
    return (<div>placeholder</div>);
  }, [
    finishPageData, currentStep, generateFinishPage, onGoogleLoggedOut, classes, i18n,
    signInToGoogle, onGoogleSelectAccount, emailAddress, logoutGoogle, accountsDropdownOptions,
    selectedAccount, onChangeAccount, containersDropdownOptions, isGTMImprovementEnabled, containerList,
    selectedContainer, onChangeContainer, gtmUIContext.AllowTagCloning, gtmUIContext.IsGTMCloningChecked,
    onTagCloningCheckboxChange,
  ]);

  return (
    <div className={`google-tag-manager-instructions ${classes.gtmInstructionsRoot}`}>
      <UiBlocker
        blocking={shouldBlockerLoading}
        loadingText={i18n.getString(_TL_('Loading...'))}
      >
        {generateStepContent}
      </UiBlocker>
    </div>
  );
};

GoogleTagManagerInstructionsUnstyled.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string,
    contentBlock: PropTypes.string,
    dropdown: PropTypes.string,
    imageCenterBlock: PropTypes.string,
    textBlock: PropTypes.string,
    gtmInstructionsRoot: PropTypes.string,
  }).isRequired,
  tag: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({}),
    isDemo: PropTypes.bool,
    Permissions: PropTypes.shape({}),
    DynamicPermissions: PropTypes.shape({
      GSILocal: PropTypes.bool,
    }),
  }).isRequired,
  deps: PropTypes.shape({
    isAllDependenciesLoaded: PropTypes.bool,
    repository: PropTypes.instanceOf(GoogleTagManagerReporitory),
    token: PropTypes.string,
  }).isRequired,
  onGoogleLoggedOut: PropTypes.func.isRequired,
  onGoogleSelectAccount: PropTypes.func.isRequired,
  updateGTMImportContext: PropTypes.func.isRequired,
  finishPageData: PropTypes.shape({ publishedVersionId: PropTypes.number }),
  gtmUIContext: PropTypes.shape({
    AllowTagCloning: PropTypes.bool,
    IsGTMCloningChecked: PropTypes.bool,
    loginEmail: PropTypes.string,
    sessionId: PropTypes.string,
  }).isRequired,
  logTrace: PropTypes.func.isRequired,
};

GoogleTagManagerInstructionsUnstyled.defaultProps = {
  finishPageData: undefined,
};

export const GoogleTagManagerInstructions = withDefaultStyles(GoogleTagManagerInstructionsUnstyled, getGtmInstructionsStyles);
